import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useSearchParams } from 'react-router';

import { AppRoutes, DEFAULT_DELAY, MIN_FILTER_YEAR, THROTTLE_LIMIT } from '@/constants';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';
import { SortDirection } from '@/types';
import { formatDate, showAlertSuccess } from '@/utils';

import { CloseIcon, PlusIcon } from '@/assets/icons';
import {
  Button,
  DateRangePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import {
  FACE_ID_REGISTRATION_STATUS_OPTIONS,
  PROFILE_STATUS_OPTIONS,
  PROFILE_TYPE_OPTIONS,
} from '@/features/face-id-profiles/constants';
import {
  FaceIdRegistrationStatus,
  ProfileStatus,
  ProfileType,
} from '@/features/face-id-profiles/enums';
import { useFaceIdProfileMutation, useProfilesSearching } from '@/features/face-id-profiles/hooks';
import {
  IFaceIdProfileSearchingPayload,
  IFaceIdProfileUpdate,
} from '@/features/face-id-profiles/interfaces';
import {
  getFaceIdRegistrationStatusLabel,
  getFaceIdRegistrationStatusVariant,
  getProfileStatusLabel,
  getProfileStatusVariant,
  getProfileTypeLabel,
} from '@/features/face-id-profiles/utils';

import { type IDateRange } from '@/components/common/DateRangePicker';

const FaceIdProfileList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);
  const [profileTypes, setProfileTypes] = useState<string[]>(
    searchParams.get('types')?.split(',').filter(Boolean) || [],
  );
  const [profileStatus, setProfileStatus] = useState<string[]>(
    searchParams.get('status')?.split(',').filter(Boolean) || [],
  );
  const [faceIdRegistrationStatus, setFaceIdRegistrationStatus] = useState<string[]>(
    searchParams.get('faceId')?.split(',').filter(Boolean) || [],
  );
  const [period, setPeriod] = useState<IDateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from') as string) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to') as string) : undefined,
  });
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || 'lastModified');
  const [isDescending, setIsDescending] = useState<boolean>(searchParams.get('desc') !== 'false');
  const dateRangePickerRef = useRef<any>(null);
  const previousFilterKeyRef = useRef<string | null>(null);

  // Handle column sorting
  const handleSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setIsDescending(true);
    } else {
      setSortBy(columnKey);
      setIsDescending(direction === 'desc');
    }
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, DEFAULT_DELAY);

  // Throttle filters and sorting
  const throttledProfileTypes = useThrottle(profileTypes, THROTTLE_LIMIT, true);
  const throttledProfileStatus = useThrottle(profileStatus, THROTTLE_LIMIT, true);
  const throttledFaceIdRegistrationStatus = useThrottle(
    faceIdRegistrationStatus,
    THROTTLE_LIMIT,
    true,
  );
  const throttledPeriod = useThrottle(period, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IFaceIdProfileSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    if (throttledProfileTypes && throttledProfileTypes.length === 1) {
      payload.profileType = Number(throttledProfileTypes[0]) as ProfileType;
    }

    if (throttledProfileStatus && throttledProfileStatus.length === 1) {
      payload.status = Number(throttledProfileStatus[0]) as ProfileStatus;
    }

    if (throttledFaceIdRegistrationStatus && throttledFaceIdRegistrationStatus.length === 1) {
      payload.faceRegistrationStatus = Number(
        throttledFaceIdRegistrationStatus[0],
      ) as FaceIdRegistrationStatus;
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    if (throttledPeriod?.from && throttledPeriod?.to) {
      payload.guestValidFrom = throttledPeriod.from;
      payload.guestValidTo = throttledPeriod.to;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledProfileTypes,
    throttledProfileStatus,
    throttledFaceIdRegistrationStatus,
    throttledPeriod,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching, isError, refetch } = useProfilesSearching(apiPayload);
  const { updateMutation } = useFaceIdProfileMutation();

  const profiles = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata.totalPages || 0, [data?.metadata.totalPages]);
  const totalItems = useMemo(() => data?.metadata.totalItems || 0, [data?.metadata.totalItems]);

  const isFiltered = useMemo(
    () =>
      searchQuery.length > 0 ||
      profileTypes.length > 0 ||
      profileStatus.length > 0 ||
      faceIdRegistrationStatus.length > 0 ||
      (period.from !== undefined && period.to !== undefined),
    [searchQuery, profileTypes, profileStatus, faceIdRegistrationStatus, period],
  );

  const isEmpty = useMemo(() => profiles.length === 0, [profiles]);

  const onClearFilters = () => {
    setSearchQuery('');
    setProfileTypes([]);
    setProfileStatus([]);
    setFaceIdRegistrationStatus([]);
    setPeriod({ from: undefined, to: undefined });
    dateRangePickerRef.current?.onClear();
  };

  const onToggleProfileStatus = async (profileId: string, newStatus: ProfileStatus) => {
    await updateMutation.mutateAsync(
      {
        profileId,
        status: newStatus,
      } as IFaceIdProfileUpdate,
      {
        onSuccess: () => {
          refetch();
          showAlertSuccess(
            `${
              newStatus === ProfileStatus.Active ? 'Activate' : 'Deactivate'
            } Profile Successfully.`,
          );
        },
      },
    );
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (profileTypes.length) params.set('types', profileTypes.join(','));
    if (profileStatus.length) params.set('status', profileStatus.join(','));
    if (faceIdRegistrationStatus.length) params.set('faceId', faceIdRegistrationStatus.join(','));
    if (period.from) params.set('from', period.from.toISOString());
    if (period.to) params.set('to', period.to.toISOString());
    if (sortBy !== 'lastModified') params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    setSearchParams(params, { replace: true });
  }, [
    currentPage,
    searchQuery,
    profileTypes,
    profileStatus,
    faceIdRegistrationStatus,
    period,
    sortBy,
    isDescending,
    setSearchParams,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledProfileTypes,
      throttledProfileStatus,
      throttledFaceIdRegistrationStatus,
      throttledPeriod?.from?.getTime() ?? null,
      throttledPeriod?.to?.getTime() ?? null,
    ]);

    if (previousFilterKeyRef.current === null) {
      // First run — store baseline, don't reset
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledProfileTypes,
    throttledProfileStatus,
    throttledFaceIdRegistrationStatus,
    throttledPeriod,
  ]);

  if (isError) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
  }

  return (
    <div className="w-full flex flex-col items-start justify-start">
      <h1 className="font-bold text-[32px] text-neutral-n10">Face Profile ID</h1>
      <div className="flex flex-row flex-wrap gap-2 items-start justify-between w-full mt-8 mb-5">
        <div className="flex flex-row flex-wrap items-start justify-start gap-2">
          <Search
            placeholder="Search by Full Name, ID or Employee ID"
            className="w-[330px] sm:w-[310px] gap-2 !h-11"
            style={{ height: '44px', paddingRight: '16px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={PROFILE_TYPE_OPTIONS}
            defaultValue={profileTypes}
            placeholder="Profile Type"
            maxCount={1}
            className="!w-fit !min-w-[280px]"
            onValueChange={(values) => {
              setProfileTypes(values);
            }}
          />
          <MultiSelect
            options={PROFILE_STATUS_OPTIONS}
            defaultValue={profileStatus}
            placeholder="Status"
            maxCount={1}
            className="!w-fit !min-w-[270px]"
            onValueChange={(values) => {
              setProfileStatus(values);
            }}
          />
          <MultiSelect
            options={FACE_ID_REGISTRATION_STATUS_OPTIONS}
            defaultValue={faceIdRegistrationStatus}
            placeholder="Face ID Registration"
            maxCount={1}
            className="!w-fit !min-w-[270px]"
            onValueChange={(values) => {
              setFaceIdRegistrationStatus(values);
            }}
          />
          <DateRangePicker
            ref={dateRangePickerRef}
            required
            hasSuffixIcon
            allowClear
            initialDateFrom={period.from}
            initialDateTo={period.to}
            onUpdate={({ range }) => setPeriod(range)}
            align="start"
            locale="en-GB"
            buttonClassName="!w-[330px] sm:!w-[270px] font-medium text-sm h-11"
            placeholder="Valid Period"
            minYear={MIN_FILTER_YEAR}
          />
          {isFiltered && (
            <Button
              variant="link"
              prefixIcon={<CloseIcon className="w-5 h-5" />}
              onClick={onClearFilters}
              className="!pl-1 !h-11"
            >
              Clear
            </Button>
          )}
        </div>
        <Button
          variant="primary"
          prefixIcon={<PlusIcon />}
          className="font-semibold text-sm px-4 rounded-lg whitespace-nowrap text-ellipsis !h-11 !w-[185px]"
          onClick={() => navigate(AppRoutes.FACE_ID_PROFILE_CREATION)}
        >
          Create New Profile
        </Button>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult />
        ) : (
          <>
            <Table className="mt-4" size="lg">
              <TableHeader>
                <TableRow>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'profileId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('profileId', direction)}
                  >
                    ID
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'fullName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('fullName', direction)}
                  >
                    Full Name
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'profileType' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('profileType', direction)}
                  >
                    Profile Type
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'status' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('status', direction)}
                  >
                    Status
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'employeeId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('employeeId', direction)}
                  >
                    Employee ID
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'department' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('department', direction)}
                  >
                    Department/Team
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'guestValidPeriod' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('guestValidPeriod', direction)}
                  >
                    Guest Valid Period
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'faceRegistrationStatus' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('faceRegistrationStatus', direction)}
                  >
                    Face ID Registration
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModified' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModified', direction)}
                  >
                    Last Modified
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModifiedBy' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModifiedBy', direction)}
                  >
                    Last Modified By
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow key={profile.profileId} rowId={profile.profileId}>
                    <TableCell className="font-medium">{profile.profileId}</TableCell>
                    <TableCell className="font-medium underline text-brand-primary-base max-w-[253px]">
                      <Link
                        to={generatePath(AppRoutes.FACE_ID_PROFILE_DETAILS, {
                          id: profile.profileId,
                        })}
                        className="block truncate"
                        title={profile.fullName}
                      >
                        {profile.fullName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">
                      {getProfileTypeLabel(profile.profileType)}
                    </TableCell>
                    <TableCell cellType="tag" tagVariant={getProfileStatusVariant(profile.status)}>
                      {getProfileStatusLabel(profile.status)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {profile.employeeId ? profile.employeeId : 'N/A'}
                    </TableCell>
                    <TableCell className="font-medium max-w-[253px]">
                      {profile.department ? (
                        <span className="block truncate" title={profile.department}>
                          {profile.department}
                        </span>
                      ) : (
                        <span className="tracking-[1px]">--</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {profile.guestValidFrom && profile.guestValidTo ? (
                        <>
                          {formatDate({
                            date: new Date(profile.guestValidFrom),
                          })}
                          {' - '}
                          {formatDate({
                            date: new Date(profile.guestValidTo),
                          })}
                        </>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell
                      cellType="status"
                      statusVariant={getFaceIdRegistrationStatusVariant(
                        profile.faceRegistrationStatus,
                      )}
                    >
                      {getFaceIdRegistrationStatusLabel(profile.faceRegistrationStatus)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatDate({
                        date: new Date(profile.lastModified),
                        hasTime: true,
                      })}
                    </TableCell>
                    <TableCell className="font-medium max-w-[185px]">
                      <span className="block truncate" title={profile.lastModifiedBy}>
                        {profile.lastModifiedBy}
                      </span>
                    </TableCell>
                    <TableCell align="right" cellType="custom" className="!p-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="w-[63px] aspect-square"
                          asChild
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          side="left"
                          alignOffset={26}
                          sideOffset={-16}
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.FACE_ID_PROFILE_UPDATE, {
                                  id: profile.profileId,
                                }),
                              )
                            }
                          >
                            Edit Profile
                          </DropdownMenuItem>
                          {profile.profileType !== ProfileType.Guest && (
                            <DropdownMenuItem
                              onClick={() => {
                                onToggleProfileStatus(
                                  profile.profileId,
                                  profile.status === ProfileStatus.Active
                                    ? ProfileStatus.Inactive
                                    : ProfileStatus.Active,
                                );
                              }}
                            >
                              {profile.status === ProfileStatus.Active
                                ? 'Deactivate Profile'
                                : 'Activate Profile'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};

export default FaceIdProfileList;
